"use client";

import React, { useState, useRef, useEffect } from "react";
import { ChevronUp, Image as ImageIcon, X } from "lucide-react";
import { Controller } from "react-hook-form";
import RTE from "./RTE";
import { useGetTourDetails } from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";

interface UploadedImage {
  id: string;
  file: File;
  preview: string;
  isDefault: boolean;
}

interface PictureMetaInformationProps {
  register: any;
  control: any;
  errors: any;
  watch: any;
}

const PictureMetaInformation: React.FC<PictureMetaInformationProps> = ({
  register,
  control,
  errors,
  watch,
}) => {
  const { user } = useAuth();
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [galleryImages, setGalleryImages] = useState<UploadedImage[]>([]);
  const [selectedRelatedTours, setSelectedRelatedTours] = useState<string[]>([]);
  const relatedItemsFieldRef = useRef<((value: string) => void) | null>(null);
  
  const imageInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Fetch tours for related items dropdown
  const { mutate: fetchTours, data: tourResults } = useGetTourDetails();
  const [tours, setTours] = useState<any[]>([]);

  useEffect(() => {
    if (user?.company_id) {
      fetchTours(
        { company_id: user.company_id, status: 1 },
        {
          onSuccess: (data: any) => {
            let toursList = [];
            if (data?.status === "success" && Array.isArray(data?.data)) {
              toursList = data.data;
            } else if (Array.isArray(data?.data)) {
              toursList = data.data;
            } else if (Array.isArray(data?.responseData?.response?.data)) {
              toursList = data.responseData.response.data;
            } else if (Array.isArray(data)) {
              toursList = data;
            }
            setTours(toursList);
          },
          onError: (err: any) => {
            console.error("Error fetching tours for related items:", err);
            setTours([]);
          },
        }
      );
    }
  }, [user?.company_id, fetchTours]);

  // Convert File to base64 for storage (or you can upload to server and get URLs)
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newImages: UploadedImage[] = [];
    
    for (const file of files) {
      const base64 = await fileToBase64(file);
      newImages.push({
        id: Date.now().toString() + Math.random(),
        file,
        preview: URL.createObjectURL(file),
        isDefault: uploadedImages.length === 0 && files.indexOf(file) === 0,
      });
    }

    const updatedImages = [...uploadedImages, ...newImages];
    setUploadedImages(updatedImages);
    
    // Update form values
    const defaultImage = updatedImages.find(img => img.isDefault) || updatedImages[0];
    const allImageNames = updatedImages.map(img => img.file.name).join(",");
    
    // Set form values using watch and control
    if (defaultImage) {
      // You'll need to use setValue from useForm, but for now we'll handle it in parent
    }
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newImages: UploadedImage[] = files.map((file) => ({
      id: Date.now().toString() + Math.random(),
      file,
      preview: URL.createObjectURL(file),
      isDefault: false,
    }));

    setGalleryImages([...galleryImages, ...newImages]);
  };

  const handleRemoveImage = (id: string, isGallery: boolean = false) => {
    if (isGallery) {
      setGalleryImages(galleryImages.filter((img) => img.id !== id));
    } else {
      const removed = uploadedImages.find((img) => img.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.preview);
      }
      const remaining = uploadedImages.filter((img) => img.id !== id);
      setUploadedImages(remaining);
      // If removed image was default, set first remaining as default
      if (removed?.isDefault && remaining.length > 0) {
        const updated = remaining.map((img, idx) => ({ ...img, isDefault: idx === 0 }));
        setUploadedImages(updated);
      }
    }
  };

  const handleSetDefault = (id: string) => {
    setUploadedImages(
      uploadedImages.map((img) => ({
        ...img,
        isDefault: img.id === id,
      }))
    );
  };

  const handleRelatedTourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(
      e.target.selectedOptions,
      (option) => option.value
    ).filter((val) => val && val !== "-- Select Related Tour Type --");
    setSelectedRelatedTours(selected);
  };

  // Sync image data to form (for submission)
  useEffect(() => {
    if (uploadedImages.length > 0) {
      const defaultImage = uploadedImages.find(img => img.isDefault) || uploadedImages[0];
      const allImageNames = uploadedImages.map(img => img.file.name).join(",");
      // These will be set in the parent component's onSubmit
    }
  }, [uploadedImages, galleryImages]);

  // Get image values for form submission
  const getImageValues = () => {
    const defaultImage = uploadedImages.find(img => img.isDefault) || uploadedImages[0];
    const allImageNames = uploadedImages.map(img => img.file.name).join(",");
    const defaultGalleryImage = galleryImages[0];
    const allGalleryImageNames = galleryImages.map(img => img.file.name).join(",");
    
    return {
      tours_image_primary: defaultImage?.file.name || null,
      tours_image: allImageNames || null,
      spots_photo_gallery_primary: defaultGalleryImage?.file.name || null,
      spots_photo_gallery: allGalleryImageNames || null,
    };
  };

  // Expose getImageValues to parent (via ref or return)
  // For now, we'll handle it in the parent component

  return (
    <div className="space-y-4">
      {/* Picture & Meta Info Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-800">
            Picture & Meta Info
          </h2>
          <button
            type="button"
            className="p-1 bg-gray-100 rounded hover:bg-gray-200"
            title="Collapse"
          >
            <ChevronUp className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="space-y-4 text-xs">
          {/* Upload Images */}
          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Upload Images (Multiselect Ctrl+Click) *
            </label>
            <div className="flex items-center gap-3">
              <input
                ref={imageInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className="px-3 py-1.5 border border-gray-300 rounded text-xs hover:bg-gray-50"
              >
                Choose Files
              </button>
              <span className="text-xs text-gray-500">
                {uploadedImages.length > 0
                  ? `${uploadedImages.length} file(s) chosen`
                  : "No file chosen"}
              </span>
            </div>

            {/* Display uploaded images with default selection */}
            {uploadedImages.length > 0 && (
              <div className="mt-3 space-y-2">
                {uploadedImages.map((img) => (
                  <div
                    key={img.id}
                    className="flex items-center gap-3 p-2 border border-gray-200 rounded"
                  >
                    <img
                      src={img.preview}
                      alt="Preview"
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1">
                      <p className="text-xs text-gray-700">{img.file.name}</p>
                      <label className="flex items-center gap-2 mt-1 cursor-pointer">
                        <input
                          type="radio"
                          name="defaultPic"
                          checked={img.isDefault}
                          onChange={() => handleSetDefault(img.id)}
                          className="h-3 w-3"
                        />
                        <span className="text-[10px] text-gray-600">
                          Default Pic
                        </span>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(img.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Hidden inputs for form submission */}
            <input
              type="hidden"
              {...register("tours_image_primary")}
              value={uploadedImages.find(img => img.isDefault)?.file.name || uploadedImages[0]?.file.name || ""}
            />
            <input
              type="hidden"
              {...register("tours_image")}
              value={uploadedImages.map(img => img.file.name).join(",")}
            />
          </div>

          {/* Spots Photo Gallery */}
          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Spots Photo Gallery
            </label>
            <div className="flex items-center gap-3">
              <input
                ref={galleryInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleGalleryUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                className="px-3 py-1.5 border border-gray-300 rounded text-xs hover:bg-gray-50"
              >
                Choose Files
              </button>
              <span className="text-xs text-gray-500">
                {galleryImages.length > 0
                  ? `${galleryImages.length} file(s) chosen`
                  : "No file chosen"}
              </span>
            </div>

            {/* Display gallery images */}
            {galleryImages.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {galleryImages.map((img) => (
                  <div key={img.id} className="relative">
                    <img
                      src={img.preview}
                      alt="Gallery"
                      className="w-20 h-20 object-cover rounded border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(img.id, true)}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                      title="Remove"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Hidden inputs for form submission */}
            <input
              type="hidden"
              {...register("spots_photo_gallery_primary")}
              value={galleryImages[0]?.file.name || ""}
            />
            <input
              type="hidden"
              {...register("spots_photo_gallery")}
              value={galleryImages.map(img => img.file.name).join(",")}
            />
          </div>
        </div>
      </div>

      {/* Related Items Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-800">
            Related Items
          </h2>
          <button
            type="button"
            className="p-1 bg-gray-100 rounded hover:bg-gray-200"
            title="Collapse"
          >
            <ChevronUp className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="text-xs">
          <label className="block text-[11px] font-medium text-gray-700 mb-1">
            Select Related tours
          </label>
          <Controller
            name="related_items"
            control={control}
            render={({ field }) => {
              // Store field.onChange in ref for use in remove handler
              relatedItemsFieldRef.current = field.onChange;
              
              // Sync selectedRelatedTours with field value
              React.useEffect(() => {
                if (field.value) {
                  const ids = String(field.value).split(",").filter(Boolean);
                  setSelectedRelatedTours(ids);
                } else {
                  setSelectedRelatedTours([]);
                }
              }, [field.value]);

              return (
                <select
                  multiple
                  value={selectedRelatedTours}
                  onChange={(e) => {
                    const selected = Array.from(
                      e.target.selectedOptions,
                      (option) => option.value
                    ).filter((val) => val && val !== "" && val !== "-- Select Related Tour Type --");
                    setSelectedRelatedTours(selected);
                    field.onChange(selected.join(","));
                  }}
                  size={6}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">-- Select Related Tour Type --</option>
                  {Array.isArray(tours) &&
                    tours.map((tour: any) => (
                      <option key={tour.id} value={String(tour.id)}>
                        {tour.tours_name}
                      </option>
                    ))}
                </select>
              );
            }}
          />
          <p className="text-[10px] text-gray-500 mt-1">
            Hold Ctrl (Cmd on Mac) to select multiple tours
          </p>
          {selectedRelatedTours.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedRelatedTours.map((tourId, index) => {
                const tour = tours.find((t: any) => String(t.id) === String(tourId));
                return (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                  >
                    {tour?.tours_name || tourId}
                    <button
                      type="button"
                      onClick={() => {
                        const updated = selectedRelatedTours.filter((t) => t !== tourId);
                        setSelectedRelatedTours(updated);
                        // Update form value via Controller
                        if (relatedItemsFieldRef.current) {
                          relatedItemsFieldRef.current(updated.join(","));
                        }
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          {errors.related_items && (
            <span className="text-red-500 text-[10px]">{errors.related_items.message}</span>
          )}
        </div>
      </div>

      {/* Meta Information Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-800">
            Meta Information
          </h2>
          <button
            type="button"
            className="p-1 bg-gray-100 rounded hover:bg-gray-200"
            title="Collapse"
          >
            <ChevronUp className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="space-y-4 text-xs">
          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Meta Title
            </label>
            <input
              {...register("meta_title")}
              type="text"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.meta_title && (
              <span className="text-red-500 text-[10px]">{errors.meta_title.message}</span>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Meta Keywords
            </label>
            <input
              {...register("meta_keywords")}
              type="text"
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Enter keywords separated by commas"
            />
            {errors.meta_keywords && (
              <span className="text-red-500 text-[10px]">{errors.meta_keywords.message}</span>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">
              Meta Description
            </label>
            <Controller
              name="meta_desc"
              control={control}
              render={({ field }) => (
                <RTE
                  content={field.value || ""}
                  onChange={(content) => field.onChange(content)}
                />
              )}
            />
            {errors.meta_desc && (
              <span className="text-red-500 text-[10px]">{errors.meta_desc.message}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PictureMetaInformation;
